import React, { useState, useEffect } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import WidgetRenderer from './WidgetRenderer';
import { GripVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function DashboardEditor({ dashboard, onUpdate, onAddWidget, onDeleteWidget }) {
  const [widgets, setWidgets] = useState([]);
  const [layout, setLayout] = useState([]);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (dashboard?.widgets) {
      setWidgets(dashboard.widgets);
      setLayout(dashboard.widgets.map(w => ({
        i: w.id,
        x: w.position.x,
        y: w.position.y,
        w: w.position.w,
        h: w.position.h,
      })));
      // Marcar como carregamento inicial para não salvar automaticamente
      setIsInitialLoad(true);
      setTimeout(() => setIsInitialLoad(false), 500);
    }
  }, [dashboard]);

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
    
    // Não salvar durante carregamento inicial
    if (isInitialLoad) {
      return;
    }
    
    // Cancelar timeout anterior
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Salvar após 2 segundos de inatividade (aumentado para evitar 429)
    const timeout = setTimeout(async () => {
      try {
        for (const item of newLayout) {
          const widget = widgets.find(w => w.id === item.i);
          if (widget) {
            await api.put(`/dashboards/widgets/${widget.id}`, {
              ...widget,
              position: { x: item.x, y: item.y, w: item.w, h: item.h },
            });
          }
        }
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erro ao atualizar layout:', error);
        toast.error('Erro ao salvar layout');
      }
    }, 2000);
    
    setSaveTimeout(timeout);
  };

  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm('Deseja realmente remover este widget?')) return;
    
    try {
      await api.delete(`/dashboards/widgets/${widgetId}`);
      setWidgets(widgets.filter(w => w.id !== widgetId));
      setLayout(layout.filter(l => l.i !== widgetId));
      toast.success('Widget removido');
      if (onDeleteWidget) onDeleteWidget(widgetId);
    } catch (error) {
      console.error('Erro ao deletar widget:', error);
      toast.error('Erro ao remover widget');
    }
  };

  if (!dashboard) {
    return (
      <div className="text-center py-12 text-slate-400">
        Selecione um dashboard para editar
      </div>
    );
  }

  if (widgets.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-400">Nenhum widget adicionado</div>
        <p className="text-sm text-slate-500 mt-2">Clique em "Adicionar Widget" para começar</p>
      </div>
    );
  }

  return (
    <div className="dashboard-editor">
      <GridLayout
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={60}
        width={1200}
        onLayoutChange={onLayoutChange}
        draggableHandle=".drag-handle"
        compactType="vertical"
        preventCollision={false}
      >
        {widgets.map(widget => (
          <div key={widget.id} className="widget-container bg-white rounded-lg shadow-sm">
            <div className="widget-header flex items-center justify-between p-2 border-b border-slate-100">
              <div className="drag-handle cursor-move flex items-center text-slate-400 hover:text-slate-600">
                <GripVertical className="w-4 h-4" />
              </div>
              <button
                onClick={() => handleDeleteWidget(widget.id)}
                className="text-slate-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="widget-content h-[calc(100%-40px)]">
              <WidgetRenderer widget={widget} />
            </div>
          </div>
        ))}
      </GridLayout>

      <style>{`
        .widget-container {
          border: 1px solid #e2e8f0;
        }
        .widget-container:hover {
          border-color: #2563eb;
        }
        .react-grid-item.react-grid-placeholder {
          background: #3b82f6;
          opacity: 0.2;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}
